import type React from "react";
import { useMemo, useState } from "react";
import { useI18n } from "@/components/languages";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { LANGUAGES } from "../languages/payload";

export const LanguageSelect: React.FC = () => {
  const { locale, setLocale } = useI18n();
  const [search, setSearch] = useState("");

  const changeLanguage = (code: string) => {
    setLocale(code);

    const params = new URLSearchParams(window.location.search);
    params.set("l", code);

    window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
  };

  const filteredLanguages = useMemo(() => {
    return LANGUAGES.filter((l) => l.label.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  const current = LANGUAGES.find((l) => l.code === locale) || LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-2 flex items-center gap-2">
          <span>{current.flag}</span>
          <span className="hidden sm:inline">{current.code.toUpperCase()}</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="
          w-64 p-2
          bg-background text-foreground
          border border-border
          shadow-lg
          dark:bg-background dark:text-foreground
          dark:border-border
        "
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search language..."
          className="
            mb-2
            bg-background
            text-foreground
            border border-border
            placeholder:text-muted-foreground
            focus-visible:ring-1 focus-visible:ring-ring
            dark:bg-background
            dark:text-foreground
            dark:border-border
          "
        />

        <div className="max-h-60 overflow-auto flex flex-col gap-1">
          {filteredLanguages.map((l) => (
            <DropdownMenuItem
              key={l.code}
              onClick={() => changeLanguage(l.code)}
              className="
                flex items-center gap-2
                cursor-pointer
                rounded-md
                px-2 py-1
                text-foreground
                hover:bg-muted
                focus:bg-muted
                dark:hover:bg-muted
                dark:focus:bg-muted
              "
            >
              <span>{l.flag}</span>
              <span>{l.label}</span>
            </DropdownMenuItem>
          ))}

          {filteredLanguages.length === 0 && (
            <div className="text-sm text-muted-foreground p-2">No results</div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
